import Ajv from "ajv/dist/2020";
import addFormats from "ajv-formats";
import type { A2uiMessage } from "../../../types";

// Import the local schemas directly
import commonTypes from "./common_types.json";
import basicCatalog from "./basic_catalog.json";
import customCatalog from "./custom_catalog.json";
import serverToClient from "./server_to_client.json";

// We keep the old valid component and function names for simple checks,
// but the core validation will happen via AJV
export const validComponentNames = Object.keys(basicCatalog.components) as [string, ...string[]];
const customFunctions = customCatalog.$defs?.functions ? Object.keys(customCatalog.$defs.functions) : [];
export const validFunctionNames = new Set([...Object.keys(basicCatalog.functions || {}), ...customFunctions]);

// Initialize AJV instance
const ajv = new Ajv({ strict: false, allErrors: true });
addFormats(ajv);

// Add the dependent schemas so references resolve correctly
ajv.addSchema(commonTypes);
ajv.addSchema(basicCatalog);
ajv.addSchema(customCatalog);

// Compile a validator against the top-level serverToClient message schema
// Note: serverToClient schema natively matches an array of messages or a single message?
// Actually server_to_client.json defines a single message in its oneOf.
const validateA2UIMessageSchema = ajv.compile(serverToClient);

export const validateFunctionsDeep = (obj: any, issues: string[]): void => {
  if (obj === null || typeof obj !== 'object') return;
  
  if (typeof obj.call === 'string') {
    if (!validFunctionNames.has(obj.call)) {
      issues.push(`Invalid function call: '${obj.call}' is not defined in the catalog.`);
    }
  }
  
  for (const key of Object.keys(obj)) {
    validateFunctionsDeep(obj[key], issues);
  }
};

export const isValidA2UIMessage = (msg: unknown): msg is A2uiMessage => {
  // 1. Validate against the official A2UI v0.9 Server-to-Client Schema
  const isValidSchema = validateA2UIMessageSchema(msg);
  
  if (!isValidSchema) {
    console.warn(`A2UI Validation Warning:`, validateA2UIMessageSchema.errors);
    return false;
  }

  // 2. Deep validate that any dynamic function calls align with the catalog functions
  // This is a business logic rule not fully captured by JSON Schema references in all cases
  if (typeof msg === 'object' && msg !== null && 'updateComponents' in msg) {
    const uc = (msg as any).updateComponents;
    if (uc && Array.isArray(uc.components)) {
      const functionIssues: string[] = [];
      validateFunctionsDeep(uc.components, functionIssues);
      if (functionIssues.length > 0) {
        console.warn(`A2UI Function Validation Warning:`, functionIssues);
        return false;
      }
    }
  }

  return true;
};
